/**
 * Theme registry. Each theme is a complete design — not just a colour swap.
 * Themes change the CSS variable palette, the typeface, the radius scale, and
 * the elevation model. Switching themes is `document.documentElement.dataset.theme = id`.
 *
 * The default theme is what the rest of the app is tuned for; the variants
 * are previews. New themes can be added here without touching components.
 */

export interface ThemeTokens {
  /** Background of the page (root). */
  bg: string
  /** Elevated surfaces (cards, sidebars, drawers). */
  bgElevated: string
  /** Subtle elevated surfaces (popovers, menus). */
  bgSubtle: string
  /** Primary text. */
  fg: string
  /** Muted / secondary text. */
  fgMuted: string
  /** Borders / dividers. */
  border: string
  /** Primary accent (buttons, links, active items). */
  accent: string
  /** Text/icon color on top of accent. */
  accentFg: string
  /** Hover state for accent. */
  accentHover: string
  /** Subtle accent (used for tinted backgrounds). */
  accentTint: string
  /** Success colour. */
  success: string
  /** Warning colour. */
  warning: string
  /** Danger colour. */
  danger: string
  /** Border radius scale. */
  radiusSm: string
  radiusMd: string
  radiusLg: string
  /** Font stack override (optional). */
  fontFamily?: string
  /** Mono font stack override (optional). */
  fontMono?: string
}

export interface ThemeDefinition {
  id: string
  name: string
  /** Tags for grouping in the picker. */
  family: 'light' | 'dark' | 'pulp' | 'neon' | 'mono' | 'film' | 'game'
  /** Marketing description shown in picker. */
  description: string
  /** Browser tab icon for this theme; the default favicon when unset. */
  favicon?: string
  tokens: ThemeTokens
}

export const THEMES: ThemeDefinition[] = [
  {
    id: 'pulp',
    name: 'OG Friday',
    family: 'pulp',
    description:
      'The 1995 movie, redrawn as a printed comic: inked panels, cream paper, Friday yellow.',
    tokens: {
      bg: '245 215 71',
      bgElevated: '255 246 224',
      bgSubtle: '252 224 99',
      fg: '18 18 26',
      fgMuted: '74 74 85',
      border: '18 18 26',
      accent: '18 18 26',
      accentFg: '245 215 71',
      accentHover: '40 40 60',
      accentTint: '237 200 56',
      success: '31 110 110',
      warning: '166 60 23',
      danger: '179 38 30',
      radiusSm: '0',
      radiusMd: '0',
      radiusLg: '0',
      fontFamily: "'Fredoka', 'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'friday-light',
    name: 'Friday Light',
    family: 'light',
    description: 'Default workspace. Clean, bright, easy on the eyes in daylight.',
    tokens: {
      bg: '250 250 251',
      bgElevated: '255 255 255',
      bgSubtle: '244 244 246',
      fg: '26 26 33',
      fgMuted: '110 110 122',
      border: '229 229 234',
      accent: '91 91 214',
      accentFg: '255 255 255',
      accentHover: '73 73 171',
      accentTint: '238 238 254',
      success: '16 185 129',
      warning: '245 158 11',
      danger: '239 68 68',
      radiusSm: '4px',
      radiusMd: '6px',
      radiusLg: '10px',
    },
  },
  {
    id: 'blade-runner',
    name: 'Blade Runner',
    family: 'film',
    description: 'Los Angeles, 2019. Rain-black night, amber neon, a red title glowing through the smog.',
    favicon: '/themes/favicon-blade-runner.svg',
    tokens: {
      bg: '10 14 19',
      bgElevated: '18 26 34',
      bgSubtle: '14 20 26',
      fg: '237 228 211',
      fgMuted: '163 157 146',
      border: '44 56 66',
      accent: '255 154 60',
      accentFg: '10 14 19',
      accentHover: '255 184 110',
      accentTint: '52 32 16',
      success: '95 211 176',
      warning: '242 193 78',
      danger: '255 106 85',
      radiusSm: '2px',
      radiusMd: '3px',
      radiusLg: '4px',
      fontFamily: "'Barlow Semi Condensed', 'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    family: 'neon',
    description: 'Night City after dark. Edgerunners yellow on Radix ink, with cyan and pink for the glitch.',
    favicon: '/themes/favicon-cyberpunk.svg',
    // Palette from Radix Colors dark scales (mauve 1-12, yellow 9-10,
    // grass/amber/red 11), so every text step clears WCAG AA on the
    // backgrounds it is paired with.
    tokens: {
      bg: '18 17 19',
      bgElevated: '26 25 27',
      bgSubtle: '35 34 37',
      fg: '238 238 240',
      fgMuted: '181 178 188',
      border: '60 57 63',
      accent: '255 230 41',
      accentFg: '18 17 19',
      accentHover: '255 255 87',
      accentTint: '45 35 5',
      success: '113 208 131',
      warning: '255 202 22',
      danger: '255 149 146',
      radiusSm: '2px',
      radiusMd: '3px',
      radiusLg: '6px',
      fontFamily: "'Chakra Petch', 'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'balatro',
    name: 'Balatro',
    family: 'game',
    description: 'Chips, mult and a swirling felt. Pixel type, chunky panels, red and blue on a dark table.',
    favicon: '/themes/favicon-balatro.svg',
    tokens: {
      bg: '23 33 35',
      bgElevated: '35 46 48',
      bgSubtle: '30 41 43',
      fg: '255 255 255',
      fgMuted: '195 207 209',
      border: '79 99 103',
      accent: '254 95 85',
      accentFg: '23 33 35',
      accentHover: '255 128 120',
      accentTint: '64 34 36',
      success: '75 194 146',
      warning: '243 185 88',
      danger: '254 95 85',
      radiusSm: '4px',
      radiusMd: '8px',
      radiusLg: '12px',
      fontFamily: "'Pixelify Sans', 'Fredoka', system-ui, sans-serif",
    },
  },
  {
    id: 'mono',
    name: 'meh theme',
    family: 'mono',
    description: 'Pure greyscale. Distraction-free. Great for focused deep work.',
    tokens: {
      bg: '255 255 255',
      bgElevated: '250 250 250',
      bgSubtle: '240 240 240',
      fg: '24 24 24',
      fgMuted: '110 110 110',
      border: '215 215 215',
      accent: '24 24 24',
      accentFg: '255 255 255',
      accentHover: '64 64 64',
      accentTint: '235 235 235',
      success: '24 24 24',
      warning: '24 24 24',
      danger: '24 24 24',
      radiusSm: '0',
      radiusMd: '0',
      radiusLg: '0',
    },
  },
]

export const DEFAULT_THEME_ID = 'pulp'

export const DEFAULT_FAVICON = '/favicon.svg'

/** `#rrggbb` for a token triplet such as '18 17 19'; used for theme-color. */
export function tokenToHex(triplet: string): string {
  return '#' + triplet.split(' ').map((v) => Number(v).toString(16).padStart(2, '0')).join('')
}

/**
 * Theme ids that were renamed. A saved choice under an old id resolves to
 * the theme that replaced it, so nobody is dropped back to the default.
 * Keep in step with the inline script in index.html.
 */
export const LEGACY_THEME_IDS: Record<string, string> = {
  neon: 'cyberpunk',
  midnight: 'blade-runner',
}

/** Resolve a saved or requested id, following renames. */
export function resolveThemeId(id: string | null | undefined): string | null {
  if (!id) return null
  const current = LEGACY_THEME_IDS[id] ?? id
  return THEMES.some((t) => t.id === current) ? current : null
}

export function findTheme(id: string | null | undefined): ThemeDefinition {
  const resolved = resolveThemeId(id)
  return THEMES.find((t) => t.id === resolved) ?? THEMES[0]!
}

/**
 * Apply a theme by writing CSS variables to :root. Variables use space-
 * separated RGB triplets so Tailwind's `rgb(var(--accent) / <alpha-value>)`
 * pattern works as-is. Non-color tokens (radii, fonts) are plain strings.
 */
export function applyThemeTokens(theme: ThemeDefinition): void {
  if (typeof document === 'undefined') return
  const t = theme.tokens
  const root = document.documentElement
  root.dataset.theme = theme.id
  root.style.setProperty('--bg', t.bg)
  root.style.setProperty('--bg-elevated', t.bgElevated)
  root.style.setProperty('--bg-subtle', t.bgSubtle)
  root.style.setProperty('--fg', t.fg)
  root.style.setProperty('--fg-muted', t.fgMuted)
  root.style.setProperty('--border', t.border)
  root.style.setProperty('--accent', t.accent)
  root.style.setProperty('--accent-fg', t.accentFg)
  root.style.setProperty('--accent-hover', t.accentHover)
  root.style.setProperty('--accent-tint', t.accentTint)
  root.style.setProperty('--success', t.success)
  root.style.setProperty('--warning', t.warning)
  root.style.setProperty('--danger', t.danger)
  root.style.setProperty('--radius-sm', t.radiusSm)
  root.style.setProperty('--radius-md', t.radiusMd)
  root.style.setProperty('--radius-lg', t.radiusLg)
  root.style.setProperty('--brand-stroke', t.accent)
  root.style.setProperty('--brand-fill', t.accentFg)
  if (t.fontFamily) {
    root.style.setProperty('--font-sans', t.fontFamily)
  } else {
    root.style.removeProperty('--font-sans')
  }
  if (t.fontMono) {
    root.style.setProperty('--font-mono', t.fontMono)
  } else {
    root.style.removeProperty('--font-mono')
  }
}